import { PostAuthRoutes } from 'c-routes';

/**
 * Groups the page names in user_activity_log (route templates from `PostAuthRoutes`) into areas
 * of the app for the usage screens.
 *
 * A template listed in AREA_BY_TEMPLATE wins; otherwise its `PostAuthRoutes` module decides. A
 * page name that isn't a known template (a renamed or removed route) is `other`.
 */
export const USAGE_AREAS = [
    'reporting',
    'campaigns',
    'schedules',
    'creatives',
    'screens',
    'organisations',
    'dashboard',
    'account',
    'admin',
    'other',
] as const;

export type UsageArea = (typeof USAGE_AREAS)[number];

const AREA_BY_TEMPLATE: Record<string, UsageArea> = {
    [PostAuthRoutes.Campaigns.CampaignReporting]: 'reporting',
    [PostAuthRoutes.Campaigns.CampaignSchedule]: 'schedules',
    [PostAuthRoutes.Campaigns.CampaignCreatives]: 'creatives',
    [PostAuthRoutes.Campaigns.CampaignDisplays]: 'screens',
    [PostAuthRoutes.Campaigns.DataStatus]: 'admin',
};

const AREA_BY_MODULE: Record<string, UsageArea> = {
    Dashboard: 'dashboard',
    Main: 'dashboard',
    Users: 'account',
    Campaigns: 'campaigns',
    Agency: 'organisations',
    Buyer: 'organisations',
    Client: 'organisations',
    Owner: 'organisations',
    Creatives: 'creatives',
    Admin: 'admin',
    admin: 'admin',
    PopDisplays: 'admin',
    Displays: 'screens',
};

const routeModules: Record<string, Record<string, string>> = PostAuthRoutes;

/** Every post-auth route template, so pages nobody visited can be listed. */
export const allRouteTemplates = (): string[] => {
    const templates = new Set<string>();
    Object.values(routeModules).forEach(module => {
        Object.values(module).forEach(template => templates.add(template));
    });
    return [...templates].sort();
};

const moduleOf = (template: string): string | undefined =>
    Object.keys(routeModules).find(module =>
        Object.values(routeModules[module]).includes(template),
    );

export const areaFor = (template: string): UsageArea => {
    const listed = AREA_BY_TEMPLATE[template];
    if (listed) return listed;
    const module = moduleOf(template);
    return (module && AREA_BY_MODULE[module]) || 'other';
};

/** Route templates with no recorded visit among `visitedNames`. */
export const neverUsed = (visitedNames: string[]): string[] => {
    const visited = new Set(visitedNames);
    return allRouteTemplates().filter(template => !visited.has(template));
};
